import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Clock, Sparkles, AlertTriangle } from "lucide-react";

export const TrialExpiryModal = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [trialStatus, setTrialStatus] = useState(null);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const checkTrialStatus = async () => {
      if (!user || user.plan !== "power" || user.plan_status !== "trial") {
        return;
      }
      
      try {
        const response = await api.get("/auth/trial-status");
        setTrialStatus(response.data);
        
        // Check if already dismissed this session
        const dismissedKey = `trial_dismissed_${user.id}`;
        const wasDismissed = sessionStorage.getItem(dismissedKey);
        
        if (!wasDismissed) {
          if (response.data.trial_expired) {
            setShowExpiredModal(true);
          } else if (response.data.show_reminder) {
            setShowReminderModal(true);
          }
        }
      } catch (error) {
        console.error("Failed to check trial status:", error);
      }
    };

    checkTrialStatus();
  }, [api, user]);

  const handleDismiss = () => {
    setDismissed(true);
    setShowExpiredModal(false);
    setShowReminderModal(false);
    if (user) {
      sessionStorage.setItem(`trial_dismissed_${user.id}`, "true");
    }
  };

  const handleSubscribe = () => {
    setShowExpiredModal(false);
    setShowReminderModal(false);
    navigate("/settings");
  };

  if (!user || user.plan !== "power" || user.plan_status !== "trial") {
    return null;
  }

  return (
    <>
      {/* Trial Expired Modal */}
      <AlertDialog open={showExpiredModal && !dismissed}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <AlertDialogTitle className="text-center text-xl">
              Your free trial has ended
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              To continue using Tally Power features like daily bank syncs, AI insights, and unlimited budgets, please subscribe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="p-4 rounded-lg bg-muted/50 space-y-2 my-4">
            <p className="text-sm font-medium">Tally Power includes:</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Daily bank syncs</li>
              <li>• AI-powered insights & predictions</li>
              <li>• Unlimited budgets & goals</li>
              <li>• Bill tracking & calendar sync</li>
            </ul>
          </div>
          
          <AlertDialogFooter className="flex-col sm:flex-col gap-2">
            <AlertDialogAction asChild>
              <Button onClick={handleSubscribe} className="w-full rounded-full">
                <Sparkles className="w-4 h-4 mr-2" />
                Subscribe – $14.99/month
              </Button>
            </AlertDialogAction>
            <Button variant="ghost" onClick={handleDismiss} className="w-full">
              Maybe later
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Trial Reminder Modal (1 day before) */}
      <AlertDialog open={showReminderModal && !dismissed}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10">
              <Clock className="w-8 h-8 text-primary" />
            </div>
            <AlertDialogTitle className="text-center text-xl">
              Your trial ends tomorrow!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              You have 1 day left on your free trial. Subscribe now to keep all your Tally Power features.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="text-center my-4">
            <p className="text-3xl font-bold text-primary">$14.99</p>
            <p className="text-sm text-muted-foreground">per month • Cancel anytime</p>
          </div>
          
          <AlertDialogFooter className="flex-col sm:flex-col gap-2">
            <AlertDialogAction asChild>
              <Button onClick={handleSubscribe} className="w-full rounded-full">
                <Sparkles className="w-4 h-4 mr-2" />
                Subscribe Now
              </Button>
            </AlertDialogAction>
            <Button variant="ghost" onClick={handleDismiss} className="w-full">
              Remind me later
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TrialExpiryModal;
